<script lang="ts">
  import { onMount } from "svelte";
  import { page } from "@inertiajs/svelte";
  import * as Sentry from "@sentry/browser";
  import { useUserInterface } from "../modules/global/models/User";

  $: current_user = $page.props.current_user;
  $: organisation = $page.props.current_organisation;
  $: sentryConfig = $page.props.sentry_config;

  let environment;
  const { href } = window.location;
  if (href.includes("dev.")) {
    environment = "staging";
  } else if (href.includes("app.")) {
    environment = "production";
  }

  onMount(() => {
    if (environment == null || !sentryConfig?.dsn) {
      return;
    }

    Sentry.init({
      dsn: sentryConfig.dsn,
      environment,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: sentryConfig.traces_sample_rate ?? 0.05,
      replaysSessionSampleRate: sentryConfig.replays_session_sample_rate ?? 0.01,
      replaysOnErrorSampleRate: sentryConfig.replays_on_error_sample_rate ?? 1.0,

      // Drop errors thrown from browser extensions injected into the page.
      denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-(web-)?extension:\/\//],

      beforeSendTransaction(event) {
        if (event.transaction?.includes("/assets/")) return null;
        return event;
      },

      beforeSend(event, hint) {
        const msg = hint?.originalException?.message || "";
        // Filter ResizeObserver errors (browser noise)
        if (msg.includes("ResizeObserver loop")) return null;
        // Filter network connectivity issues
        if (msg.match(/Failed to fetch|NetworkError|Load failed/)) return null;
        // Filter browser extension interference. Match the actual frame URL
        // schemes (chrome-extension://, moz-extension://, safari-extension://) —
        // not "extensions/", which never appears in these URLs.
        if (
          event?.exception?.values?.[0]?.stacktrace?.frames?.some((f) =>
            /^(chrome|moz|safari(-web)?)-extension:\/\//.test(f.filename ?? "")
          )
        ) { return null; }
        return event;
      },
    });

    Sentry.setUser({
      id: current_user?.id,
      email: current_user?.email,
      name: useUserInterface(current_user).fullname(),
      role: useUserInterface(current_user).role,
      created_at: current_user?.created_at,
      organisation: {
        name: organisation?.name,
        type: organisation?.type,
        id: organisation?.id,
      },
    });
  });
</script>
